import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import logo from '@assets/images/scriptix-blue-logo@2-1.png';
import logoDark from '@assets/images/scriptix-white-400x99.png';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { PATHS } from '../../router/paths.ts';

type License = {
  /** Name of the open source component / model. */
  name: string;
  /** Short description of what it is and how Scriptix uses it. */
  description: string;
  /** SPDX-style license identifier. */
  license: string;
  /** Copyright holder / party that must be attributed. */
  copyright: string;
  /** Canonical link to the project or model card. */
  url: string;
  /** Link to the full license text, when it differs from the project URL. */
  licenseUrl?: string;
};

/**
 * Third-party open source components used by Scriptix whose licenses require
 * attribution or carry notable terms (e.g. CC-BY, Apache-2.0, LGPL/GPL).
 * Permissively licensed (e.g. plain MIT) dependencies are intentionally
 * omitted, as they do not require attribution here.
 */
const LICENSES: License[] = [
  {
    name: 'NVIDIA Canary 1B v2',
    description:
      'Multilingual speech-to-text and translation model used for automatic transcription.',
    license: 'CC-BY-4.0',
    copyright: '© NVIDIA Corporation',
    url: 'https://huggingface.co/nvidia/canary-1b-v2',
    licenseUrl: 'https://creativecommons.org/licenses/by/4.0/'
  },
  {
    name: 'NVIDIA Parakeet',
    description:
      'Streaming and batch automatic speech recognition model used for real-time transcription.',
    license: 'CC-BY-4.0',
    copyright: '© NVIDIA Corporation',
    url: 'https://huggingface.co/nvidia/parakeet-tdt-1.1b',
    licenseUrl: 'https://creativecommons.org/licenses/by/4.0/'
  },
  {
    name: 'NVIDIA NeMo',
    description:
      'Conversational AI toolkit providing the training and inference framework for the ASR models above.',
    license: 'Apache-2.0',
    copyright: '© NVIDIA Corporation',
    url: 'https://github.com/NVIDIA/NeMo',
    licenseUrl: 'https://github.com/NVIDIA/NeMo/blob/main/LICENSE'
  },
  {
    name: 'OpenAI Whisper',
    description:
      'Speech recognition model used for transcription and language detection.',
    license: 'MIT',
    copyright: '© OpenAI',
    url: 'https://github.com/openai/whisper',
    licenseUrl: 'https://github.com/openai/whisper/blob/main/LICENSE'
  },
  {
    name: 'Kaldi',
    description:
      'Speech recognition toolkit powering the legacy real-time transcription engine.',
    license: 'Apache-2.0',
    copyright: '© Johns Hopkins University and the Kaldi contributors',
    url: 'https://github.com/kaldi-asr/kaldi',
    licenseUrl: 'https://github.com/kaldi-asr/kaldi/blob/master/COPYING'
  },
  {
    name: 'FFmpeg',
    description:
      'Multimedia framework used for audio and video decoding, normalisation and conversion.',
    license: 'LGPL-2.1-or-later / GPL-2.0-or-later',
    copyright: '© The FFmpeg developers',
    url: 'https://ffmpeg.org/',
    licenseUrl: 'https://www.ffmpeg.org/legal.html'
  }
];

const LicensesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour, theme } = useThemeContext();

  return (
    <div
      className="py-4 min-h-screen"
      style={{ backgroundColor: getColour('baseLayer') }}
    >
      <div className="w-full min-h-screen flex items-start justify-center sm:p-8 p-4">
        <div
          className="w-full max-w-4xl rounded-2xl shadow-md overflow-y-auto"
          style={{ backgroundColor: getColour('secondLayer') }}
        >
          <header
            className="py-8 px-6 text-center"
            style={{
              background:
                theme === 'dark'
                  ? `linear-gradient(to bottom, rgba(30, 58, 138, 0.2), ${getColour(
                      'baseLayer'
                    )})`
                  : 'linear-gradient(to bottom, #dbeafe, #eff6ff)'
            }}
          >
            <img
              src={theme === 'dark' ? logoDark : logo}
              alt="Scriptix Logo"
              className="mx-auto w-44 cursor-pointer mb-6"
              onClick={() => navigate(PATHS().HOME)}
            />
            <h1
              className="text-2xl font-semibold mb-2"
              style={{ color: getColour('normalText') }}
            >
              {t('Open Source Licenses')}
            </h1>
            <p
              className="text-sm max-w-2xl mx-auto"
              style={{ color: getColour('inactiveText') }}
            >
              {t(
                'Scriptix is built on top of open source software. Below we acknowledge the third-party components and models we rely on, together with their licenses and required attributions.'
              )}
            </p>
          </header>

          <div className="p-6 space-y-4">
            {LICENSES.map(item => (
              <div
                key={item.name}
                className="rounded-xl p-5 border"
                style={{
                  backgroundColor: getColour('thirdLayer'),
                  borderColor: getColour('border')
                }}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg font-semibold hover:underline"
                    style={{ color: getColour('activeText') }}
                  >
                    {item.name}
                  </a>
                  <span
                    className="text-xs font-medium px-2.5 py-1 rounded-full border"
                    style={{
                      color: getColour('normalText'),
                      borderColor: getColour('border')
                    }}
                  >
                    {item.license}
                  </span>
                </div>
                <p
                  className="text-sm mb-3"
                  style={{ color: getColour('normalText') }}
                >
                  {t(item.description)}
                </p>
                <div
                  className="text-xs flex flex-wrap items-center gap-x-4 gap-y-1"
                  style={{ color: getColour('inactiveText') }}
                >
                  <span>{item.copyright}</span>
                  {item.licenseUrl && (
                    <a
                      href={item.licenseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                      style={{ color: getColour('activeText') }}
                    >
                      {t('View license')}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          <footer
            className="px-6 pb-8 pt-2 text-center text-xs"
            style={{ color: getColour('inactiveText') }}
          >
            {t(
              'License names link to the official terms. All trademarks and models are the property of their respective owners.'
            )}
          </footer>
        </div>
      </div>
    </div>
  );
};

export default LicensesPage;
